import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Bx, Typo } from '@curry-group/mui-curcuma';
import { CircularProgress } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/pro-light-svg-icons';
import { AppFrameContent, AppFrameHeader } from '../../components/frame';
import { AppFrameFooter } from '../../components/frame/footer';
import { Header, ICommunicationAction } from '../../components/communication/header';
import { Messages } from '../../components/communication/messages';
import { CommunicationInput } from '../../components/communication/input';
import { FramedContent } from '../../components/framed-content';
import { GeneralModal } from '../../components/modals/GeneralModal';
import { InputModal } from '../../components/modals/InputModal';
import {
  sendBotChatMessage,
  initBotChatSession,
  discardBotChat,
  saveBotChatRequest,
} from '../../data/actions/botChat';
import { buildBotMessage } from '../../data/reducer/botChat';
import { IMessageModel } from '../../model/communication/Message';
import { useIsMobile } from '../../helper/hooks/isMobile';
import { useBotChatInfoButton } from './infoButton';
import { BotChatDisclaimer } from './complaint';
import { BotChatStart } from './start';

const BOT_CHAT_TITLE = 'Medtec Online-Bot';
const BOT_CHAT_SUBTITLE = 'MTO Medtec Online GmbH';

export interface IBotChatDetailProps {
  /**
   * URL of the container the bot chat is mounted in (e.g. "/bot"). Everything
   * navigational is derived from it, so the chat is no longer bound to the
   * "chat" container it used to live in.
   */
  basePath: string;
}

export const BotChatDetail: React.FC<IBotChatDetailProps> = ({ basePath }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobile = useIsMobile();

  const fullName = useSelector(state => state.foundation?.profile?.fullName);
  const userId = useSelector(state => state.foundation?.profile?.userId) || '';
  const messages = useSelector(state => state.botChat?.messages) || [];
  const currentBotResponse = useSelector(state => state.botChat?.currentBotResponse) || '';
  const botWorking = useSelector(state => state.botChat?.botWorking) || false;
  const saving = useSelector(state => state.botChat?.saving) || false;
  const active = useSelector(state => state.botChat?.active) || false;
  const savedCommunicationId = useSelector(state => state.botChat?.savedCommunicationId);
  const sessionId = useSelector(state => state.botChat?.sessionId);

  const [inputValue, setInputValue] = useState('');
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const unblockRef = useRef<(() => void) | null>(null);

  // Always start the fresh /botchat route from a clean slate when arriving
  // here from a saved chat — the slice still has that chat's messages,
  // sessionId and savedCommunicationId from SavedBotChatDetail's hydrate, so
  // without this reset the welcome screen wouldn't appear and a follow-up
  // message would silently get appended to the previous saved chat via the
  // saga's append branch. We also init when the slice is inactive (first
  // visit / after discard), but skip re-init for an in-progress fresh session
  // (active=true, no savedCommunicationId) so the user keeps their typing.
  useEffect(() => {
    if (savedCommunicationId || !active) {
      dispatch(initBotChatSession());
    }
  }, [dispatch, active, savedCommunicationId]);

  useEffect(() => {
    const unblock = history.block((location) => {
      if (messages.length === 0 || saving) return undefined;
      // Skip the save-dialog when the user is just switching between bot
      // chats (fresh /botchat or a saved bot chat URL). Saved sessions
      // auto-persist new turns server-side, so there's nothing to "save"
      // in that direction. Detection: stay within the same bot container.
      const dest = location?.pathname || '';
      if (basePath && dest.startsWith(basePath)) return undefined;
      setPendingNavigation(dest);
      setLeaveModalOpen(true);
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      unblock();
    };
  }, [history, messages.length, saving, basePath]);

  const unblockNavigation = () => {
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
  };

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || botWorking) return;
    dispatch(sendBotChatMessage({ content: trimmed }));
    setInputValue('');
  }, [dispatch, inputValue, botWorking]);

  const handleLeave = () => {
    setLeaveModalOpen(false);
    unblockNavigation();
    dispatch(discardBotChat());
    if (pendingNavigation) {
      history.push(pendingNavigation);
    } else {
      history.push(basePath);
    }
    setPendingNavigation(null);
  };

  const handleOpenSaveModal = () => {
    setLeaveModalOpen(false);
    setSaveModalOpen(true);
  };

  const handleSaveConfirm = () => {
    if (!saveTitle.trim()) return;
    // Saved under the plain title: in the bot rubric every entry is a bot session,
    // so a " - Bot" suffix would only repeat the context.
    const title = saveTitle.trim();
    unblockNavigation();
    dispatch(saveBotChatRequest({ title }));
    setSaveModalOpen(false);
    setSaveTitle('');
    if (pendingNavigation) {
      history.push(pendingNavigation);
    } else {
      history.push(basePath);
    }
    setPendingNavigation(null);
  };

  const handleSaveCancel = () => {
    setSaveModalOpen(false);
    setSaveTitle('');
  };

  const messagesWithStreaming = useMemo<IMessageModel[]>(() => {
    if (botWorking && currentBotResponse) {
      return [...messages, buildBotMessage(currentBotResponse, 'bot-streaming')];
    }
    return messages;
  }, [messages, botWorking, currentBotResponse]);

  // BMBFMTOL-1530: Solange noch keine Nachricht existiert, wird die eigenständige Startansicht (BotChatStart)
  // gezeigt. Mit der ersten Nachricht wechselt die Ansicht in den normalen Chat-Frame.
  const isWelcome = messages.length === 0;
  const welcomeInputRef = useRef<HTMLDivElement | null>(null);
  const footerInputRef = useRef<HTMLDivElement | null>(null);
  const chatContentRef = useRef<HTMLDivElement | null>(null);
  const morphFromRef = useRef<DOMRect | null>(null);

  const handleWelcomeSend = useCallback(() => {
    if (!inputValue.trim() || botWorking) return;
    morphFromRef.current = welcomeInputRef.current?.getBoundingClientRect() || null;
    handleSend();
  }, [inputValue, botWorking, handleSend]);

  // "Morph": das Eingabefeld gleitet von der zentrierten Position in den Footer, der Chat blendet ein.
  useLayoutEffect(() => {
    const from = morphFromRef.current;
    morphFromRef.current = null;
    if (isWelcome || !from || !footerInputRef.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const to = footerInputRef.current.getBoundingClientRect();
    const options: KeyframeAnimationOptions = { duration: 350, easing: 'cubic-bezier(0.2, 0, 0, 1)' };
    footerInputRef.current.animate?.(
      [{ transform: `translateY(${from.top - to.top}px)` }, { transform: 'translateY(0)' }],
      options
    );
    chatContentRef.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], options);
  }, [isWelcome]);

  const metaActions: ICommunicationAction[] = [
    {
      specificType: 'Btn',
      onClick: handleOpenSaveModal,
      icon: <FontAwesomeIcon icon={faSave} />,
      title: 'Session speichern',
      variant: 'contained',
      color: 'primary',
      disabled: messages.length === 0 || saving,
    },
  ];

  const infoButton = useBotChatInfoButton();
  const headerActions: ICommunicationAction[] = [infoButton.action];

  return (
    <>
      <AppFrameHeader>
        <Header
          working={false}
          // Desktop keeps the session list on screen, so the back arrow has
          // nowhere useful to go; on mobile it leads back to that list.
          hideBackButton={!isMobile}
          title={BOT_CHAT_TITLE}
          subtitle={BOT_CHAT_SUBTITLE}
          tabs={[]}
          selectedTabValue="main"
          actions={headerActions}
          metaActions={metaActions}
          metaInfo={<BotChatDisclaimer messages={messages} sessionId={sessionId} />}
        />
      </AppFrameHeader>

      <AppFrameContent>
        {isWelcome ? (
          <BotChatStart
            fullName={fullName}
            value={inputValue}
            onChange={setInputValue}
            onSubmit={handleWelcomeSend}
            disabled={botWorking || saving}
            inputRef={welcomeInputRef}
          />
        ) : (
          <FramedContent
            height="100%"
            minHeight="100%"
            innerBoxProps={{ minHeight: '100%', height: '100%' }}
          >
            <div ref={chatContentRef} style={{ height: '100%' }}>
              <Messages
                currentUserId={userId}
                messages={messagesWithStreaming}
                allowThreading={false}
                allowQuotes={false}
                allowVoting={false}
                inlineThreading={false}
                manageMessages={false}
                isBotChat
                dateDivider
                atStart
                atEnd
                isPublic={false}
                isListed={false}
                onScrollBottomChange={() => {}}
              />
              {botWorking && !currentBotResponse && (
                <Bx display="flex" alignItems="center" mt={1} pl={2}>
                  <CircularProgress size={16} color="primary" />
                  <Bx ml={1.5}>
                    <Typo variant="body2" color="textSecondary">
                      Medtec Online-Bot schreibt ...
                    </Typo>
                  </Bx>
                </Bx>
              )}
            </div>
          </FramedContent>
        )}
      </AppFrameContent>

      {!isWelcome && (
        <AppFrameFooter>
          <div ref={footerInputRef}>
            <CommunicationInput
              placeholder="Nachricht an den Bot verfassen"
              canWriteMessage
              allowAttachments={false}
              disabled={botWorking || saving}
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSend}
            />
          </div>
        </AppFrameFooter>
      )}

      <GeneralModal
        open={leaveModalOpen}
        headline="Session beenden"
        content="Möchten Sie diese Session speichern? Ansonsten geht Ihre Unterhaltung verloren."
        commitText="Speichern"
        cancelText="Verlassen"
        close={() => {
          setLeaveModalOpen(false);
          setPendingNavigation(null);
        }}
        cancel={handleLeave}
        commit={handleOpenSaveModal}
      />

      <InputModal
        open={saveModalOpen}
        headline="Session speichern"
        content="Geben Sie einen Titel für die Session ein:"
        inputText={saveTitle}
        commitText="Speichern"
        cancelText="Abbrechen"
        onInputTextChange={setSaveTitle}
        close={handleSaveCancel}
        cancel={handleSaveCancel}
        commit={handleSaveConfirm}
      />

      {infoButton.modal}
    </>
  );
};
